/* eslint-disable no-param-reassign */
import { types as t, Instance } from 'mobx-state-tree';
import DateType from '~/mst/models/abstract/date_time';
import { ModelV2 } from '~/mst/models/abstract/model';
import Syncable from '~/mst/models/abstract/syncable';
import Fetchable from '~/mst/models/abstract/fetchable';
import { MessageModel, ToolExecutionStatus } from './message';

export const AttachedFileModel = t.model('AttachedFile', {
  file_id: t.string,
  filename: t.string
});

const ThreadModel = t
  .compose(
    t.model({
      messages: t.array(MessageModel),
      name: t.string,
      namePending: t.optional(t.boolean, false),
      status: t.maybeNull(t.string),
      processingPhase: t.maybeNull(t.enumeration('ProcessingPhase', ['thinking', 'responding', 'transcribing'])),
      created_at: t.maybeNull(DateType),
      pinned: t.optional(t.boolean, false),
      pinned_at: t.maybeNull(DateType),
      total_tokens: t.optional(t.maybeNull(t.number), null),
      token_limit: t.optional(t.number, 400000),
      limitReached: t.optional(t.boolean, false),
      attachedFiles: t.optional(t.array(AttachedFileModel), [], [null, undefined])
    }),
    ModelV2,
    Fetchable(),
    Syncable
  )
  .volatile(() => ({
    // Number of files currently uploading (drives the composer spinner)
    uploadingCount: 0
  }))
  .actions((self) => ({
    // Message management actions
    addMessageWithId(messageData: {
      id: string;
      role: 'user' | 'assistant';
      content: string;
      timestamp: string;
      toolExecution?: {
        currentTool?: string;
        status: ToolExecutionStatus;
        description?: string;
      };
    }) {
      // Convert role to type for MessageModel
      const message = {
        id: messageData.id,
        type: messageData.role === 'user' ? ('human' as const) : ('ai' as const),
        content: messageData.content,
        timestamp: messageData.timestamp,
        toolExecution: messageData.toolExecution || null
      };
      self.messages.push(message);
    },

    updateMessageContent(messageId: string, content: string) {
      const message = self.messages.find((m) => m.id === messageId);
      if (message) {
        message.setContent(content);
      }
    },

    updateMessageRunId(messageId: string, runId: string) {
      const message = self.messages.find((m) => m.id === messageId);
      if (message) {
        message.setRunId(runId);
      }
    },

    updateMessageToolExecution(
      messageId: string,
      toolData: {
        currentTool?: string;
        status?: ToolExecutionStatus;
        description?: string;
      }
    ) {
      const message = self.messages.find((m) => m.id === messageId);
      if (message) {
        message.updateToolExecution(toolData);
      }
    },

    // Thread state management actions
    setStatus(status: string | null) {
      self.status = status;
    }
  }));

export interface IThreadModel extends Instance<typeof ThreadModel> {}
export interface IThreadModelSnapshotOut extends Instance<typeof ThreadModel> {}

export default ThreadModel;
